import type { PublicacaoDto } from "@base/contracts";
import { CircleAlert, ExternalLink, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  IconePlataforma,
  ROTULO_PLATAFORMA,
  ROTULO_PLATAFORMA_CURTO,
} from "../../../shared/branding/icone-plataforma";
import { falhasDoGrupo } from "../domain/calendario-agenda";

const ROTULO_STATUS: Record<PublicacaoDto["status"], string> = {
  rascunho: "Rascunho",
  aprovado: "Aprovado",
  queued: "Na fila",
  scheduled: "Agendado",
  publicado: "Publicado",
  falhou: "Falhou",
};

const ROTULO_TIPO: Record<PublicacaoDto["tipo"], string> = {
  imagem: "Imagem",
  carrossel: "Carrossel",
  reels: "Reels",
};

type Densidade = "dia" | "semana" | "mes";

type Props = {
  readonly publicacao: PublicacaoDto;
  readonly redes?: readonly PublicacaoDto[];
  readonly densidade?: Densidade;
  readonly onVerPreview?: (publicacao: PublicacaoDto) => void;
  readonly onVerErro?: (falhas: readonly PublicacaoDto[]) => void;
};

/**
 * Item da agenda. Vista dia: card amplo. Semana/mês: compacto.
 * Varias redes do mesmo post aparecem juntas, com icone por status.
 */
export function CartaoPublicacaoAgenda({
  publicacao,
  redes,
  densidade = "semana",
  onVerPreview,
  onVerErro,
}: Props) {
  const itens = redes && redes.length > 0 ? redes : [publicacao];
  const titulo =
    publicacao.nome.trim() || publicacao.legenda.trim() || "(sem nome)";
  const horario = publicacao.agendarPara
    ? new Date(publicacao.agendarPara).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "--:--";
  const legenda = publicacao.legenda.trim();
  const mostrarLegenda =
    legenda.length > 0 && legenda !== publicacao.nome.trim();
  const falhas = falhasDoGrupo(itens);
  const publicados = itens.filter((p) => p.status === "publicado");
  const todosFalharam = falhas.length === itens.length;
  const todosPublicados = publicados.length === itens.length;
  const statusGrupo = statusDoGrupo(itens);
  const linkUnico =
    publicados.length === 1 ? publicados[0]!.instagramUrl?.trim() || null : null;
  const motivos = falhas
    .map((p) => {
      const motivo = p.motivoFalha?.trim();
      if (!motivo) return null;
      if (falhas.length === 1) return motivo;
      return `${ROTULO_PLATAFORMA[p.plataforma]}: ${motivo}`;
    })
    .filter((m): m is string => Boolean(m));

  if (densidade === "mes") {
    return (
      <li
        className={cn(
          "flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] leading-tight",
          "bg-muted/80 text-foreground",
          todosFalharam && "bg-destructive/12 text-destructive",
          todosPublicados && "bg-success/15 text-success",
        )}
        title={`${horario} · ${itens.map((p) => ROTULO_PLATAFORMA_CURTO[p.plataforma]).join(" ")} · ${titulo}${falhas.length ? " · Com falha" : ""}${todosPublicados ? " · Publicado" : ""}`}
      >
        {falhas.length > 0 ? (
          <CircleAlert className="size-3 shrink-0" aria-hidden />
        ) : null}
        <IconesDasRedes itens={itens} tamanho="size-3" />
        <span
          className={cn(
            "shrink-0 tabular-nums",
            todosFalharam || todosPublicados
              ? "text-inherit opacity-80"
              : "text-muted-foreground",
          )}
        >
          {horario}
        </span>
        <span className="min-w-0 truncate">{titulo}</span>
      </li>
    );
  }

  if (densidade === "dia") {
    return (
      <li
        className={cn(
          "rounded-xl border p-4 sm:p-5",
          todosFalharam && "border-destructive/40 bg-destructive/5",
          todosPublicados && "border-success/40 bg-success/5",
          !todosFalharam && !todosPublicados && "border-border bg-background",
        )}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 gap-4 sm:gap-5">
            <div className="shrink-0 text-center sm:w-20 sm:pt-0.5">
              <p className="text-2xl font-semibold tracking-tight tabular-nums text-foreground sm:text-3xl">
                {horario}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">horário</p>
            </div>
            <div className="grid min-w-0 flex-1 gap-2">
              <div className="flex min-w-0 items-start gap-2">
                {falhas.length > 0 ? (
                  <CircleAlert
                    className="mt-1 size-5 shrink-0 text-destructive"
                    aria-label="Publicação com falha"
                  />
                ) : null}
                <h3 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">
                  {titulo}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <IconesDasRedes itens={itens} tamanho="size-3.5" comRotulo />
                <Badge variant="outline" className="font-normal">
                  {ROTULO_TIPO[publicacao.tipo]}
                </Badge>
                <Badge variant={varianteBadgeStatus(statusGrupo.chave)}>
                  {statusGrupo.chave === "falhou" ? (
                    <span className="inline-flex items-center gap-1">
                      <CircleAlert className="size-3" aria-hidden />
                      {statusGrupo.rotulo}
                    </span>
                  ) : (
                    statusGrupo.rotulo
                  )}
                </Badge>
                {publicacao.midias.length > 0 ? (
                  <span className="text-xs text-muted-foreground">
                    {publicacao.midias.length} mídia
                    {publicacao.midias.length === 1 ? "" : "s"}
                  </span>
                ) : null}
              </div>
              {mostrarLegenda ? (
                <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                  {legenda}
                </p>
              ) : null}
              {motivos.length > 0 ? (
                <div className="space-y-2" role="alert">
                  {motivos.map((motivo) => (
                    <p key={motivo} className="text-sm text-destructive">
                      {motivo}
                    </p>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto">
            {falhas.length > 0 && onVerErro ? (
              <Button
                type="button"
                variant="outline"
                className="w-full cursor-pointer border-destructive/40 text-destructive hover:bg-destructive/10 sm:w-auto"
                aria-label={`Ver erros de ${titulo}`}
                onClick={() => onVerErro(falhas)}
              >
                <CircleAlert className="size-4" aria-hidden />
                Ver erros
              </Button>
            ) : null}
            {linkUnico ? (
              <Button
                type="button"
                variant="outline"
                className="w-full cursor-pointer border-success/40 text-success hover:bg-success/10 sm:w-auto"
                asChild
              >
                <a
                  href={linkUnico}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Abrir postagem de ${titulo}`}
                >
                  <ExternalLink className="size-4" aria-hidden />
                  Abrir postagem
                </a>
              </Button>
            ) : null}
            {onVerPreview ? (
              <Button
                type="button"
                variant="outline"
                className="w-full shrink-0 cursor-pointer sm:w-auto"
                aria-label={`Ver preview de ${titulo}`}
                onClick={() => onVerPreview(publicacao)}
              >
                <Eye className="size-4" aria-hidden />
                Ver preview
              </Button>
            ) : null}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-lg border px-3 py-2.5",
        todosFalharam && "border-destructive/40 bg-destructive/5",
        todosPublicados && "border-success/40 bg-success/5",
        !todosFalharam && !todosPublicados && "border-border/80 bg-background",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {falhas.length > 0 ? (
          <CircleAlert
            className="size-4 shrink-0 text-destructive"
            aria-label="Publicação com falha"
          />
        ) : null}
        <span
          className={cn(
            "w-12 shrink-0 text-sm tabular-nums",
            todosPublicados ? "text-success" : "text-muted-foreground",
            todosFalharam && "text-destructive",
          )}
        >
          {horario}
        </span>
        <p
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 truncate text-sm font-medium",
            todosPublicados ? "text-success" : "text-foreground",
            todosFalharam && "text-destructive",
          )}
        >
          <IconesDasRedes itens={itens} tamanho="size-3.5" />
          <span className="min-w-0 truncate">{titulo}</span>
        </p>
        {falhas.length > 0 && onVerErro ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 cursor-pointer text-destructive"
            aria-label={`Ver erros de ${titulo}`}
            onClick={(e) => {
              e.stopPropagation();
              onVerErro(falhas);
            }}
          >
            <CircleAlert className="size-4" aria-hidden />
          </Button>
        ) : null}
        {linkUnico ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 cursor-pointer text-success"
            asChild
          >
            <a
              href={linkUnico}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Abrir postagem de ${titulo}`}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="size-4" aria-hidden />
            </a>
          </Button>
        ) : null}
        {onVerPreview ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 cursor-pointer"
            aria-label={`Ver preview de ${titulo}`}
            onClick={(e) => {
              e.stopPropagation();
              onVerPreview(publicacao);
            }}
          >
            <Eye className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      {motivos.length > 0 ? (
        <p className="line-clamp-2 text-xs text-destructive" role="alert">
          {motivos[0]}
          {motivos.length > 1 ? ` (+${motivos.length - 1})` : ""}
        </p>
      ) : null}
    </li>
  );
}

function IconesDasRedes({
  itens,
  tamanho,
  comRotulo = false,
}: {
  readonly itens: readonly PublicacaoDto[];
  readonly tamanho: string;
  readonly comRotulo?: boolean;
}) {
  if (comRotulo && itens.length === 1) {
    const p = itens[0]!;
    return (
      <Badge
        variant="outline"
        className="inline-flex items-center gap-1.5 font-normal"
      >
        <IconePlataforma
          plataforma={p.plataforma}
          tom={tomDoStatus(p.status)}
          className={tamanho}
          titulo={`${ROTULO_PLATAFORMA[p.plataforma]}: ${ROTULO_STATUS[p.status]}`}
        />
        {ROTULO_PLATAFORMA_CURTO[p.plataforma]}
      </Badge>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      {itens.map((p) => (
        <IconePlataforma
          key={p.id}
          plataforma={p.plataforma}
          tom={tomDoStatus(p.status)}
          className={tamanho}
          titulo={`${ROTULO_PLATAFORMA[p.plataforma]}: ${ROTULO_STATUS[p.status]}`}
        />
      ))}
    </span>
  );
}

function tomDoStatus(
  status: PublicacaoDto["status"],
): "sucesso" | "falha" | "neutro" {
  if (status === "publicado") return "sucesso";
  if (status === "falhou") return "falha";
  return "neutro";
}

function statusDoGrupo(itens: readonly PublicacaoDto[]): {
  readonly chave: PublicacaoDto["status"] | "misto";
  readonly rotulo: string;
} {
  const falhas = itens.filter((p) => p.status === "falhou").length;
  const publicados = itens.filter((p) => p.status === "publicado").length;
  if (itens.length > 1 && falhas > 0 && falhas < itens.length) {
    return { chave: "falhou", rotulo: "Falhou em parte" };
  }
  if (publicados === itens.length) {
    return { chave: "publicado", rotulo: ROTULO_STATUS.publicado };
  }
  if (falhas === itens.length) {
    return { chave: "falhou", rotulo: ROTULO_STATUS.falhou };
  }
  const primeiro = itens[0]!.status;
  return { chave: primeiro, rotulo: ROTULO_STATUS[primeiro] };
}

function varianteBadgeStatus(
  status: PublicacaoDto["status"] | "misto",
): "destructive" | "successOutline" | "secondary" {
  if (status === "falhou") return "destructive";
  if (status === "publicado") return "successOutline";
  return "secondary";
}
